// What leaves the service, seen from the calendar, the week plan and a theme's page.
import { z } from "zod";
import { IsoDateTime, PageQuery, Uuid, pageOf } from "./common";

/** What the calendar shows and filters. Posts come from the queue, because they are what a person drags. */
export const OUTGOING_KINDS = ["post", "ad", "email", "message"] as const;
export const OutgoingKind = z.enum(OUTGOING_KINDS);
export type OutgoingKind = z.infer<typeof OutgoingKind>;

/** One visible range: a month grid with its edges is 42 days. */
export const MAX_OUTGOING_DAYS = 62;
const DAY_MS = 86_400_000;

export const OutgoingQuery = PageQuery.extend({ from: IsoDateTime, to: IsoDateTime }).refine((q) => Date.parse(q.to) > Date.parse(q.from) && Date.parse(q.to) - Date.parse(q.from) <= MAX_OUTGOING_DAYS * DAY_MS, { path: ["to"], message: "range_too_long" });
export type OutgoingQuery = z.input<typeof OutgoingQuery>;

/** Which engine holds it: each has its own words for a status, and its own page. */
export const OUTGOING_SOURCES = ["cell", "promotion", "campaign"] as const;

export const OutgoingItemDto = z.object({
  id: Uuid,
  kind: OutgoingKind.exclude(["post"]),
  source: z.enum(OUTGOING_SOURCES),
  /** Its real time, or the time the plan meant for a draft nobody has set a time on yet. */
  at: IsoDateTime,
  planned: z.boolean(),
  title: z.string(),
  status: z.string(),
  /** A path in the app. */
  link: z.string(),
});
export type OutgoingItemDto = z.infer<typeof OutgoingItemDto>;
export const OutgoingPage = pageOf(OutgoingItemDto);
export type OutgoingPage = z.infer<typeof OutgoingPage>;

export const PLAN_OUTPUT_STATES = ["not_started", "working", "made", "waiting", "failed"] as const;
export const PlanOutputState = z.enum(PLAN_OUTPUT_STATES);
export type PlanOutputState = z.infer<typeof PlanOutputState>;

export const PlanOutputTarget = z.object({
  kind: z.enum(["creative", "campaign", "promotion"]),
  id: Uuid,
  status: z.string(),
  link: z.string(),
  /** An e-mail with nobody to send it to yet. */
  needsAudience: z.boolean(),
});
export type PlanOutputTarget = z.infer<typeof PlanOutputTarget>;

export const PlanItemOutputDto = z.object({
  itemId: Uuid,
  state: PlanOutputState,
  errorCode: z.string().nullable(),
  targets: z.array(PlanOutputTarget),
  canRetry: z.boolean(),
});
export type PlanItemOutputDto = z.infer<typeof PlanItemOutputDto>;
export const WeekPlanOutputs = z.object({ items: z.array(PlanItemOutputDto) });
export type WeekPlanOutputs = z.infer<typeof WeekPlanOutputs>;

export const ThemeOutputResults = z.object({
  reach: z.number().nullable(),
  impressions: z.number().nullable(),
  clicks: z.number().nullable(),
  leads: z.number().nullable(),
  sent: z.number().nullable(),
  opened: z.number().nullable(),
  spendMinor: z.number().nullable(),
  currency: z.string().nullable(),
});
export type ThemeOutputResults = z.infer<typeof ThemeOutputResults>;

export const ThemeOutputDto = z.object({
  id: Uuid,
  kind: z.enum(["post", "ad", "email"]),
  source: z.enum(["creative", ...OUTGOING_SOURCES]),
  title: z.string(),
  status: z.string(),
  at: IsoDateTime.nullable(),
  link: z.string(),
  /** Null: nothing measured, which is not the same as zero. */
  results: ThemeOutputResults.nullable(),
});
export type ThemeOutputDto = z.infer<typeof ThemeOutputDto>;
export const ThemeOutputs = z.object({ themeId: Uuid, title: z.string(), items: z.array(ThemeOutputDto) });
export type ThemeOutputs = z.infer<typeof ThemeOutputs>;
